<template>
  <div class="i-intro-section">
    <div class="container">
      <div class="top">
        <div class="descriptor">
          <div class="badge"><div class="inner">Your results are in!</div></div>
          <div>
            Turns out you're <br />{{ getArticle(personalityType) }} <strong>{{ personalityType }}.</strong>
          </div>
        </div>
        <div class="analysis">
          <div
            v-if="!!personalityDescriptors.length"
            class="text"
          >
            <div>We analyzed your answers and discovered that you are:</div>
            <div class="row-list">
              <div
                v-for="descriptor in personalityDescriptors"
                :key="descriptor"
                class="detail"
              >
                {{ descriptor }}
              </div>
            </div>
          </div>
          <div class="action">
            <iBaseButton
              class="button"
              v-bind="buttonStyle"
              text="View Your Report Snapshot"
              @clicked="$emit('scroll-to-snapshot')"
            />
            <div class="rating"><img :src="ImageStar" />4.5 stars | Loved by 150K+ users</div>
          </div>
        </div>
        <div
          v-if="isMdOrLarger"
          class="graph"
        >
          <img
            :src="CircleGraph"
            alt="Customer satisfaction statistics"
          />
        </div>
      </div>
      <div class="bottom">
        <div>Featured In</div>
        <div class="featured">
          <template
            v-for="featured in arrayOfFeatured"
            :key="featured"
          >
            <img
              class="feat"
              :src="featured"
            />
          </template>
        </div>
      </div>
    </div>
    <div
      v-if="!isMdOrLarger"
      class="mobile"
    >
      <img
        class="image-mob"
        :src="Stats"
        alt="Customer satisfaction statistics"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { useReportStore } from "@/stores/report"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointLg, breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { getArticle } from "@/composables/getArticle"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import ImageStar from "@/assets/icons/icon-single-slashed-star.svg"
import Featured7 from "@/assets/welcome/featured-cuny.svg"
import Featured1 from "@/assets/welcome/featured-pace.svg"
import Featured5 from "@/assets/welcome/featured-abc.svg"
import Featured3 from "@/assets/welcome/featured-cbs.svg"
import Featured2 from "@/assets/welcome/featured-fox.svg"
import Featured4 from "@/assets/welcome/featured-dj.svg"
import Featured6 from "@/assets/welcome/featured-nbc.svg"
import CircleGraph from "@/assets/welcome/circle-graph.webp"
import Stats from "@/assets/welcome/circle-graph-mob.webp"

// EMITS
defineEmits(["scroll-to-snapshot"])

// COMPOSABLES AND STORES
const isLargerThanTablet = useIsScreenSize(breakpointLg)
const isMdOrLarger = useIsScreenSize(breakpointMd)
const reportStore = useReportStore()

//CONSTANTS
const arrayOfFeatured = [Featured1, Featured2, Featured3, Featured4, Featured5, Featured6, Featured7]

//COMPUTED
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isLargerThanTablet.value ? "max-content" : "100%",
    padding: isLargerThanTablet.value ? "8px 70px" : "10px 6px",
    size: "medium"
  }
})
const personalityDescriptors = computed(() => reportStore.reportData?.results?.personalityDescriptors)
const personalityType = computed(() => reportStore.reportData?.results?.personalityType)
</script>

<style scoped lang="scss">
.i-intro-section {
  @include flex(column, center, stretch, $gap: 24px);

  background-image: url("@/assets/confirm/bg-gradient-multi.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  padding: 0 15px 25px;

  @media screen and (min-width: $breakpoint-md) {
    padding: 0 15px 60px;
    background: unset;
  }

  @media screen and (min-width: $breakpoint-lg) {
    align-items: center;
  }

  .container {
    @include border-radius-lg;

    max-width: 960px;
    width: 100%;
    background-color: #fff6;
    overflow: hidden;
    box-shadow: 0 0 40px 40px #53535312;

    @media screen and (min-width: $breakpoint-md) {
      background-image: url("@/assets/confirm/bg-gradient-multi.png");
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center center;
    }

    @media screen and (min-width: $breakpoint-xl) {
      max-width: 1410px;
    }

    .top {
      @include flex(column, center, center, $gap: 25px);

      border-bottom: 3px solid $black100;
      text-align: center;
      padding: 24px 15px;

      @media screen and (min-width: $breakpoint-sm) {
        padding: 45px;
      }

      @media screen and (min-width: $breakpoint-md) {
        border-bottom: 3px solid $black50;
        background-color: #ffffff78;
        gap: 35px;
        padding: 45px 45px 0;
      }

      .graph {
        max-width: 900px;
      }

      .descriptor {
        @include flex(column, center, center, $gap: 4px);
        @include display-sm;

        @media screen and (min-width: $breakpoint-sm) {
          @include display-lg;
        }

        @media screen and (min-width: $breakpoint-md) {
          @include display-xl;
        }

        .badge {
          @include border-radius-xxl;
          @include text-sm;

          font-weight: 600;
          width: fit-content;
          background-image: $blue-purple-gradient;
          margin: 0 3px;
          text-transform: uppercase;

          .inner {
            @include border-radius-xxl;

            padding: 0 12px;
            background-color: #ffffffda;
            mix-blend-mode: screen;
          }
        }
      }

      .analysis {
        @include flex(column, center, center, $gap: 25px);

        .text {
          @include flex(column, center, center, $gap: 12px);
          @include text-md;

          font-weight: 700;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-lg;

            gap: 8px;
          }

          @media screen and (min-width: $breakpoint-md) {
            @include text-xl;
          }

          .row-list {
            @include flex(row, center, center, wrap, $gap: 6px 20px);
            @include text-sm;

            font-weight: 400;

            .detail {
              @include flex(row, start, center);

              color: $black950;
              list-style: none;

              &::before {
                content: "";
                display: inline-block;
                height: 20px;
                width: 25px;
                background-image: url("@/assets/icons/icon-green-check.svg");
                background-repeat: no-repeat;
              }
            }
          }
        }

        .action {
          @include flex(column, center, center, $gap: 8px);

          .rating {
            @include text-sm;

            img {
              vertical-align: text-bottom;
              margin-right: 3px;
            }
          }
        }
      }
    }

    .bottom {
      @include flex(column, center, center, $gap: 10px);
      @include text-sm;

      font-weight: 700;
      padding: 24px 15px;

      @media screen and (min-width: $breakpoint-md) {
        background-color: $white;
        padding: 24px;
      }

      .featured {
        @include flex(row, center, center, wrap, $gap: 2px 15px);

        @media screen and (min-width: $breakpoint-md) {
          @include flex(row, center, center, wrap, $gap: 40px);
        }

        .feat {
          height: 16px;

          @media screen and (min-width: $breakpoint-sm) {
            height: unset;
          }
        }
      }
    }
  }

  .mobile {
    .image-mob {
      width: 100%;
    }
  }
}
</style>
